import { useLoaderData, useSearchParams } from "react-router";

import { SWRValue } from "~/.server/caching.server";
import { getClient } from "~/contentful/contentful.server";
import { getPageMeta } from "~/seo/servers/seo.server";

import {
  useContentfulInspectorMode,
  useContentfulLiveUpdates,
} from "@contentful/live-preview/react";

import type { TypeOurStoryPageSkeleton } from "~/contentful/compiled";
import { RichTextRender } from "~/contentful/components/rich-text";
import { StickySection } from "~/contentful/components/sticky-section";
import { OUR_STORY_CONTENT } from "~/contentful/contents";

import Image from "../../components/ui/image";

export const loader = async ({
  request,
  context: { waitUntil, locale },
}: LoaderArgs) => {
  const url = new URL(request.url);
  const preview = url.searchParams.get("preview");
  const client = getClient(!!preview);
  const page = url.searchParams.get("page");
  const pageContent = await SWRValue({
    key: `posts-page=${page}-${locale.id}`,
    cb: async () => {
      return await client.getEntry<TypeOurStoryPageSkeleton>(OUR_STORY_CONTENT);
    },
    waitUntil,
    cacheControl: "s-maxage=30, stale-while-revalidate",
  });

  const seo = getPageMeta({
    type: "article",
    options: {
      url: request.url,
      title: pageContent.fields.title,
      description: pageContent.fields.title,
    },
  });

  return { content: pageContent.fields, seo };
};

const OurStory = () => {
  const { content } = useLoaderData<typeof loader>();
  const liveContent = useContentfulLiveUpdates(content);
  const [searchParams] = useSearchParams();
  const preview = !!searchParams.get("preview");
  const contentData = preview ? liveContent : content;
  const mediaFile = contentData.media?.fields.file;
  const inspectorProps = useContentfulInspectorMode();

  return (
    <div className="container mx-auto">
      <div className="mx-auto w-3/4 gap-4">
        <div
          className="mb-10 mt-8"
          {...inspectorProps({
            entryId: contentData.media?.sys.id,
            fieldId: "name",
          })}
        >
          <h1 className="text-center text-3xl font-bold">
            {contentData.title}
          </h1>
          <div className="mb-4 text-center text-lg">
            <RichTextRender
              entryId={contentData.media?.sys.id ?? ""}
              content={contentData.description}
            />
          </div>
          {mediaFile?.contentType === "image/jpeg" ? (
            <Image
              key={mediaFile?.fileName}
              className="h-128 w-full object-cover"
              alt={mediaFile?.fileName}
              src={mediaFile?.url}
            />
          ) : (
            <video
              muted
              key={mediaFile?.fileName}
              autoPlay
              src={mediaFile?.url}
              loop
              controls
              className="h-128 w-full object-cover"
            />
          )}
        </div>
        {contentData.sections &&
          contentData.sections.map(
            section =>
              section && (
                <StickySection key={section.fields.title} content={section} />
              ),
          )}
      </div>
    </div>
  );
};

export default OurStory;
